import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EmptyState } from '@/shared/ui/EmptyState';
import { agentsApi } from '@/modules/agents/api/agentsApi';
import { agentsKeys } from '@/modules/agents/queryKeys';
import { reviewSmsApi, type PerformanceRange } from '../api/reviewSmsApi';
import { reviewKeys } from '../queryKeys';
import { TextFunnel } from '../analytics/TextFunnel';
import {
  AnalyticsCard,
  ByDelayCard,
  ByProviderCard,
  Delta,
  Kpi,
  RangeSwitch,
  RatingMixCard,
  ReplyByHourCard,
  WeeklyByMethodCard,
} from '../analytics/AnalyticsCards';
import { rateText } from '../analytics/format';
import type { TextStepKey } from '../analytics/types';
import a from '../analytics/analytics.module.css';
import styles from './TextRequests.module.css';

/** The practice's ladder: the full step list is the Command Center's. */
const PRACTICE_STEPS: TextStepKey[] = [
  'confirmed',
  'sent',
  'replied',
  'rated',
  'approved',
  'google',
  'matched',
];

/**
 * Betty's proposals waiting on the practice. Shown only when there is one: her practice-facing
 * skills are not switched on yet, and an always-empty card promised advice that never came.
 */
function Guidance({ locationId }: { locationId: string }) {
  const tasks = useQuery({
    queryKey: agentsKeys.tasks(locationId),
    queryFn: () => agentsApi.listTasks(locationId),
    enabled: !!locationId,
    staleTime: 60_000,
  });
  const open = (tasks.data ?? []).filter(
    (t) =>
      t.agentType === 'reviews' && (t.state === 'awaiting_approval' || t.state === 'needs_review'),
  );
  if (open.length === 0) return null;
  return (
    <AnalyticsCard title="Betty’s guidance">
      <div className={styles.guidanceList}>
        {open.map((t) => (
          <div key={t.id} className={styles.guidanceCard}>
            <span className={styles.guidanceTitle}>{t.title}</span>
            <div className={styles.guidanceActions}>
              <Link to="/tasks" className={styles.subTab}>
                Review in Tasks
              </Link>
            </div>
          </div>
        ))}
      </div>
    </AnalyticsCard>
  );
}

/**
 * Performance (PRD 5 §4.4): from confirmed visit to a review live on Google, for the visits
 * confirmed in the range. Numbers follow each visit to the end, so the last few days keep filling
 * in as conversations finish.
 */
export function PerformanceTab({ locationId }: { locationId: string }) {
  const [range, setRange] = useState<PerformanceRange>('30d');
  const perf = useQuery({
    queryKey: reviewKeys.smsPerformance(locationId, range),
    queryFn: () => reviewSmsApi.getPerformance(locationId, range),
    enabled: !!locationId,
    staleTime: 60_000,
  });
  const view = perf.data;
  const k = view?.kpis;
  const p = view?.previous;

  return (
    <div className={a.stack}>
      <div className={a.toolbar}>
        <span className={styles.hint}>
          Visits confirmed in the last {range.replace('d', ' days')}, compared with the{' '}
          {range.replace('d', ' days')} before
        </span>
        <RangeSwitch value={range} onChange={setRange} />
      </div>
      {perf.isLoading && <p className={styles.muted}>Loading…</p>}
      {perf.isError && (
        <p className={styles.muted}>Couldn’t load performance right now. Try again in a minute.</p>
      )}
      {/* Outside the empty state: Betty's setup proposals come before there are any visits. */}
      <Guidance locationId={locationId} />
      {view && k && p && k.confirmedVisits === 0 && p.confirmedVisits === 0 ? (
        <EmptyState
          title="No confirmed visits in this range yet"
          description="Confirm visits on the Today tab and the numbers fill in here."
        />
      ) : (
        view &&
        k &&
        p && (
          <>
            <div className={a.kpis}>
              <Kpi
                label="Reply rate"
                value={rateText(k.replyRate)}
                sub={
                  <>
                    <Delta current={k.replyRate} previous={p.replyRate} /> {k.replied} of{' '}
                    {k.delivered || k.texted} delivered
                  </>
                }
              />
              <Kpi
                label="Visit → review"
                value={rateText(k.visitToReviewRate)}
                sub={`${k.matched} of ${k.confirmedVisits} confirmed visits became a Google review`}
              />
              <Kpi
                label="Happy patients who posted"
                value={rateText(k.happyPostedRate)}
                sub={`of ${k.fiveStar} who gave 5★`}
              />
              <Kpi
                label="Confirmed same day"
                value={rateText(k.sameDayConfirmedRate)}
                sub="the rest caught up within 30 days"
              />
              <Kpi
                label="Checked in after going quiet"
                value={k.checkInsSent}
                sub={`${k.repliedAfterCheckIn} replied · ${k.tappedGoogleAfterCheckIn} went on to Google`}
              />
            </div>
            <div className={a.split}>
              <AnalyticsCard
                title="From visit to Google review"
                sub="Every confirmed visit in this range, step by step"
              >
                <TextFunnel view={view} keys={PRACTICE_STEPS} />
                <p className={a.note}>
                  “Live on Google” counts new Google reviews matched to a conversation by name,
                  timing and wording. Google doesn’t say who wrote a review, so matches are
                  estimates.
                </p>
              </AnalyticsCard>
              <RatingMixCard view={view} />
            </div>
            <div className={a.grid2}>
              <ReplyByHourCard view={view} />
              <ByDelayCard view={view} />
            </div>
            <div className={a.grid2}>
              <ByProviderCard view={view} />
              <WeeklyByMethodCard weekly={view.weekly} />
            </div>
          </>
        )
      )}
    </div>
  );
}
